import React, { Component } from 'react';
import { bool, func, object } from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { getBuyerId, getUserType } from 'dibs-cookie-jar';
import { isEmailOnly } from 'dibs-cookie-jar/exports/isEmailOnly';
import get from 'lodash.get';

import MessageBanner from '../MessageBanner/MessageBanner';
import TopBar from '../TopBar/TopBar';
import BottomBar from '../BottomBar/BottomBar';
import Drawer from '../Drawer/Drawer';

import { actionCreators } from '../../actions';
import * as userTracking from '../../utils/userTracking';
import chat from '../../utils/chat';
import {
    hasValidLocalUserGeoInfo,
    setLocalUserGeoInfo,
    getUserZipCode,
} from '../../utils/userGeoInfo';
import { TRADE } from '../../../exports/userTypeEnums';
import {
    getCurrencyPreference,
    setSessionCurrency,
    setLocalCurrency,
    getSessionCurrency,
} from '../../utils/preferredCurrency';
import {
    getIsFirstTimeVisitor as getMessageBannerIsFirstTimeVisitor,
    getPreviewVariables as getMessageBannerPreviewVariables,
} from '../MessageBanner/messageHelpers';

import styles from './Header-style.scss';

class Header extends Component {
    constructor(props) {
        super(props);

        this.state = {
            hasError: false,
            forceSlideIn: false,

            fragmentVariables: {
                isClient: false,
                hasUserId: false,
                showMarketingMessages: this.props.showMarketingMessages,
                hasMarketingBannerPreviewVariables: false,
                marketingBannerPreviewUserType: '',
                marketingBannerPreviewSetId: '',
                marketingBannerIsFirstTimeVisitor: false,
                userId: '',
                userIds: [],
                fetchRegionalInfo: false,
                fetchTradeNav: false,
                fetchNav: false,
                fetchSubNav: false,
                zipCode: '',
            },
        };

        this.handleTopBarMouseOver = this.handleTopBarMouseOver.bind(this);
        this.setupRegionalInfo = this.setupRegionalInfo.bind(this);
    }

    componentDidMount() {
        this.refetchViewer();
    }

    componentDidUpdate(prevProps) {
        if (this.props.updateUserState && !prevProps.updateUserState) {
            this.refetchViewer();
        }
        if (get(this.props.viewer, 'user.isVerifiedTrade') && !get(prevProps.viewer, 'user')) {
            this.props.updateUserTypeTrade();
        }
        if (get(this.props.viewer, 'user.isVip') && !get(prevProps.viewer, 'user')) {
            this.props.updateUserTypeVip();
        }

        if (
            this.state.fragmentVariables.isClient &&
            !this.props.initialTrackingFired &&
            hasValidLocalUserGeoInfo(this.props.viewer.regionalInfo)
        ) {
            const { viewer } = this.props;
            const userId = getBuyerId(document.cookie);
            const hasUserId = !!userId;
            const hasUser = !!get(viewer, 'user');

            if (!hasUserId) {
                userTracking.init({ viewer });
                this.props.setInitialTrackingFired();
            } else if (hasUser) {
                const { user } = viewer;
                chat.fillUserData(viewer, user);
                userTracking.init({ viewer, user });
                this.props.setInitialTrackingFired();
            }
        }
    }

    componentDidCatch(error) {
        if (window.newrelic) {
            const newrelicError = new Error();
            newrelicError.message = `dibs-buyer-layout header error boundary triggered: ${
                error.message
            }`;
            newrelicError.stack = error.stack;

            window.newrelic.noticeError(newrelicError);
        }

        this.setState({ hasError: true });
    }

    getClassnames({ forceDisableStickyNav, hasTopBarOnly }) {
        const { disableStickyNav } = this.props;

        return {
            wrapperClasses: classnames({
                [styles.wrapper]: true,
            }),
            headerClasses: classnames({
                [styles.header]: true,
                [styles.disableSticky]: disableStickyNav || forceDisableStickyNav,
                [styles.hasTopBarOnly]: hasTopBarOnly,
            }),
            shadowClasses: classnames({
                [styles.shadow]: true,
                [styles.disableSticky]: disableStickyNav || forceDisableStickyNav,
                [styles.hasTopBarOnly]: hasTopBarOnly,
            }),
            bottomWrapperClasses: classnames({
                [styles.bottomWrapper]: true,
                [styles.disableSticky]: disableStickyNav || forceDisableStickyNav,
            }),
        };
    }

    /**
     * The viewer should be fetched a max of (2) times:
     *
     * (1) On componentDidMount to fetch client-only fragments, and user fragments if already logged in
     * (2) On componentDidUpdate if user logs in
     */
    refetchViewer() {
        const { viewer, isUserTypeTrade } = this.props;
        const userId = getBuyerId(document.cookie) || '';
        const cookieUserType = getUserType(document.cookie) || '';
        const hasUserId = !!userId;
        const fetchRegionalInfo =
            (!hasUserId && !getSessionCurrency()) || !hasValidLocalUserGeoInfo(viewer.regionalInfo);
        const renderTradeNav = cookieUserType === TRADE || isUserTypeTrade;
        this.props.setIsEmailOnly(isEmailOnly(document.cookie));

        let fragmentVariables = {
            ...this.state.fragmentVariables,
            isClient: true,
            hasUserId,
            userId,
            userIds: [userId],
            marketingBannerIsFirstTimeVisitor: !hasUserId && getMessageBannerIsFirstTimeVisitor(),
            fetchRegionalInfo,
            zipCode: getUserZipCode(),
        };

        const {
            userType: messageBannerUserType,
            setId: messageBannerSetId,
        } = getMessageBannerPreviewVariables();

        if (messageBannerUserType && messageBannerSetId) {
            fragmentVariables = {
                ...fragmentVariables,
                hasMarketingBannerPreviewVariables: true,
                marketingBannerPreviewSetId: messageBannerSetId,
                marketingBannerPreviewUserType: messageBannerUserType,
            };
        }

        this.setState(
            {
                fragmentVariables,
            },
            () => {
                this.props.relay.refetch(
                    this.state.fragmentVariables,
                    {
                        ...this.state.fragmentVariables,
                        fetchNav: !renderTradeNav,
                        fetchTradeNav: renderTradeNav,
                    },
                    this.setupRegionalInfo
                );
            }
        );
    }
    setupRegionalInfo() {
        const { viewer } = this.props;

        const user = get(viewer, 'user');
        const userCurrency = get(user, 'profile.currency');
        const regionalCurrency = get(viewer, 'regionalInfo[0].currency');

        if (!user && !getSessionCurrency()) {
            setSessionCurrency(regionalCurrency);
        } else if (userCurrency) {
            setLocalCurrency(userCurrency);
        }

        this.props.populateCurrency(getCurrencyPreference({ user, regionalCurrency }));

        if (this.state.fragmentVariables.fetchRegionalInfo) {
            setLocalUserGeoInfo(viewer);
        }
    }
    handleTopBarMouseOver() {
        this.setState({ forceSlideIn: true }, () => {
            this.setState({
                forceSlideIn: false,
            });
        });
    }

    render() {
        const { disableStickyNav, viewer, hideHeaderFooter } = this.props;
        const user = get(viewer, 'user') || null;
        const regionalInfo = get(viewer, 'regionalInfo[0]') || null;
        const { fragmentVariables, forceSlideIn, hasError } = this.state;
        const {
            isClient,
            hasUserId,
            showMarketingMessages,
            userId,
            userIds,
            fetchRegionalInfo,
        } = fragmentVariables;
        const forceDisableStickyNav = hasError;
        const hasUser = !!user;
        const hasRootPendingRefetch =
            (!hasUser && hasUserId) || (fetchRegionalInfo && !get(viewer, 'regionalInfo'));
        const BottomWrapperTag = disableStickyNav || forceDisableStickyNav ? 'div' : Drawer;
        const bottomWrapperDefaultProps = {
            onStateChange: this.props.setDrawerState,
        };
        const bottomWrapperProps =
            disableStickyNav || forceDisableStickyNav
                ? { ...bottomWrapperDefaultProps }
                : { ...bottomWrapperDefaultProps, forceSlideIn };

        const {
            wrapperClasses,
            headerClasses,
            shadowClasses,
            bottomWrapperClasses,
        } = this.getClassnames({ forceDisableStickyNav, hasTopBarOnly: hasError });

        if (this.state.hasError) {
            return (
                <div className={wrapperClasses} hidden={hideHeaderFooter}>
                    <div className={headerClasses}>
                        <TopBar
                            hasError
                            disableStickyNav
                            hasRootPendingRefetch={hasRootPendingRefetch}
                            hasUser={hasUser}
                            hasUserId={hasUserId}
                            viewer={viewer}
                            isClient={isClient}
                            userId={userId}
                            user={user}
                        />
                    </div>
                    <div className={shadowClasses} />
                </div>
            );
        }

        return (
            <div className={wrapperClasses} hidden={hideHeaderFooter}>
                <div className={headerClasses}>
                    <TopBar
                        disableStickyNav={disableStickyNav}
                        hasRootPendingRefetch={hasRootPendingRefetch}
                        hasUser={hasUser}
                        hasUserId={hasUserId}
                        viewer={viewer}
                        isClient={isClient}
                        userId={userId}
                        user={user}
                        regionalInfo={regionalInfo}
                        onMouseOver={this.handleTopBarMouseOver}
                    />
                    <BottomWrapperTag className={bottomWrapperClasses} {...bottomWrapperProps}>
                        <BottomBar
                            viewer={viewer}
                            isClient={isClient}
                            hasUserId={hasUserId}
                            userIds={userIds}
                            userId={userId}
                            hasRootPendingRefetch={hasRootPendingRefetch}
                        />
                        <div id="nav-bottom-marker" />
                    </BottomWrapperTag>
                </div>
                <div className={shadowClasses} />
                {showMarketingMessages ? (
                    <MessageBanner
                        hasUserId={hasUserId}
                        isBelow
                        isClient={isClient}
                        showMarketingMessages={showMarketingMessages}
                        user={user}
                        userId={userId}
                        viewer={viewer}
                    />
                ) : null}
            </div>
        );
    }
}

Header.propTypes = {
    disableStickyNav: bool,
    hideHeaderFooter: bool,
    initialTrackingFired: bool,
    isClient: bool,
    isMobile: bool,
    isUserTypeTrade: bool,
    setInitialTrackingFired: func,
    showMarketingMessages: bool,
    relay: object,
    updateUserTypeTrade: func,
    updateUserTypeVip: func,
    populateCurrency: func,
    updateUserState: bool,
    viewer: object,
    setIsEmailOnly: func.isRequired,
    setDrawerState: func,
};

const mapStateToProps = ({ header }) => ({
    isMobile: header.isMobile,
    disableStickyNav: header.disableStickyNav,
    hideHeaderFooter: header.hideHeaderFooter,
    updateUserState: header.updateUserState,
    initialTrackingFired: header.initialTrackingFired,
    isUserTypeTrade: header.isUserTypeTrade,
});

const mapDispatchToProps = dispatch => ({
    updateUserTypeTrade: () => {
        dispatch(actionCreators.setUserTypeTrade(true));
    },
    updateUserTypeVip: () => {
        dispatch(actionCreators.setUserTypeVip(true));
    },
    setIsEmailOnly: emailOnly => {
        dispatch(actionCreators.setIsEmailOnly(emailOnly));
    },
    setInitialTrackingFired: () => {
        dispatch(actionCreators.setInitialTrackingFired());
    },
    populateCurrency: currency => dispatch(actionCreators.populateCurrency(currency)),
    setDrawerState: isDrawerOpen => {
        dispatch(actionCreators.setDrawerState(isDrawerOpen));
    },
});

Header = createRefetchContainer(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(Header),
    {
        viewer: graphql`
            fragment Header_viewer on Viewer {
                ...BottomBar_viewer
                ...TopBar_viewer
                ...userTracking_viewer @relay(mask: false)
                ...BottomBar_viewer
                ... on Viewer @include(if: $hasUserId) {
                    ...chat_viewer @relay(mask: false)
                }
                ... on Viewer @include(if: $showMarketingMessages) {
                    ...MessageBanner_viewer
                }
                user(userId: $userId) @include(if: $hasUserId) {
                    ...userTracking_user @relay(mask: false)
                    ...chat_user @relay(mask: false)
                    ... on User @include(if: $showMarketingMessages) {
                        ...MessageBanner_user
                    }
                    ...TopBar_user
                    isVip
                    isVerifiedTrade
                    profile {
                        currency
                    }
                }
                regionalInfo(userId: $userId) @include(if: $fetchRegionalInfo) {
                    ...userGeoInfo_regionalInfo @relay(mask: false)
                    ...userTracking_regionalInfo @relay(mask: false)
                    currency
                }
            }
        `,
    },
    graphql`
        query HeaderRefetchQuery(
            $isClient: Boolean = false
            $userId: String!
            $userIds: [String]!
            $hasUserId: Boolean!
            $showMarketingMessages: Boolean!
            $hasMarketingBannerPreviewVariables: Boolean = false
            $marketingBannerPreviewUserType: String = ""
            $marketingBannerPreviewSetId: String = ""
            $marketingBannerIsFirstTimeVisitor: Boolean = false
            $fetchRegionalInfo: Boolean!
            $fetchTradeNav: Boolean!
            $fetchNav: Boolean!
            $fetchSubNav: Boolean!
            $previewKey: String = ""
            $zipCode: String!
        ) {
            viewer {
                ...Header_viewer
            }
        }
    `
);

export default Header;
